import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

const KEYS = [["public_key", "op.key_public"], ["secret_key", "op.key_secret"], ["hmac_secret", "op.key_hmac"], ["api_key", "op.key_api"]];

function KeyField({ k, label, cfg, value, onChange, t }) {
  const src = cfg[`${k}_source`];
  const setText = src ? t("op.key_set", { last4: cfg[`${k}_last4`] }) : t("op.key_not_set");
  return (
    <label className="grid gap-1 text-sm">
      <span>{t(label)}</span>
      <input type="password" autoComplete="new-password" className="input" value={value} onChange={(e) => onChange(e.target.value)} dir="ltr"
        placeholder={src ? `${setText}${src === "env" ? ` (${t("op.key_from_env")})` : ""} · ${t("op.key_replace")}` : t("op.key_not_set")}
        data-testid={`pg-${k}`} />
      <span className="text-xs" style={{ color: "var(--text-muted)" }} data-testid={`pg-${k}-status`}>{setText}</span>
    </label>
  );
}

function Field({ label, children }) {
  return <label className="grid gap-1 text-sm">{label}{children}</label>;
}

export function PaymentGatewayPanel() {
  const { t } = useT();
  const [cfg, setCfg] = useState(null);
  const [keys, setKeys] = useState({});
  const [busy, setBusy] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  useEffect(() => { api.get("/payment-gateway/config").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!cfg) return <div className="py-6 opacity-60">{t("op.loading")}</div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const body = { enabled: cfg.enabled, provider: cfg.provider, mode: cfg.mode, api_base_url: cfg.api_base_url, amount_unit: cfg.amount_unit,
        integration_ids: String(cfg.integration_ids || "").split(",").map((x) => x.trim()).filter(Boolean),
        hold_minutes: Number(cfg.hold_minutes), default_deposit_percent: cfg.default_deposit_percent, min_online_payment: cfg.min_online_payment,
        link_expiry_days: Number(cfg.link_expiry_days), ...Object.fromEntries(Object.entries(keys).filter(([, v]) => v)) };
      const { data } = await api.put("/payment-gateway/config", body);
      setCfg(data); setKeys({}); toast.success(t("op.saved"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    try { const { data } = await api.post("/payment-gateway/test"); setTestMsg(`${data.ok ? t("op.test_ok") : t("op.test_problem")}: ${data.message}`); }
    catch (e) { setTestMsg(formatApiError(e)); }
  };

  return (
    <div className="grid gap-4" data-testid="payment-gateway-panel" style={{ maxWidth: 640 }}>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="pg-enabled" /> {t("op.enabled")}</label>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("op.provider")}>
          <select className="input" value={cfg.provider} onChange={(e) => set("provider", e.target.value)} data-testid="pg-provider">
            <option value="paymob">Paymob</option>
            {cfg.mock_allowed && <option value="mock">{t("op.mock")}</option>}
          </select>
        </Field>
        <Field label={t("op.mode")}>
          <select className="input" value={cfg.mode} onChange={(e) => set("mode", e.target.value)} data-testid="pg-mode">
            <option value="test">{t("op.mode_test")}</option><option value="live">{t("op.mode_live")}</option>
          </select>
        </Field>
        <Field label={t("op.api_address")}>
          <input className="input" dir="ltr" value={cfg.api_base_url} onChange={(e) => set("api_base_url", e.target.value)} data-testid="pg-base-url" />
        </Field>
        <Field label={t("op.amount_unit")}>
          <select className="input" value={cfg.amount_unit} onChange={(e) => set("amount_unit", e.target.value)} data-testid="pg-amount-unit">
            <option value="baisa">{t("op.unit_baisa")}</option><option value="omr">{t("op.unit_omr")}</option>
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label={t("op.integration_ids")}>
            <input className="input" dir="ltr" value={Array.isArray(cfg.integration_ids) ? cfg.integration_ids.join(", ") : cfg.integration_ids} onChange={(e) => set("integration_ids", e.target.value)} data-testid="pg-integration-ids" />
          </Field>
        </div>
        <Field label={t("op.hold_minutes")}>
          <input type="number" min={5} className="input" value={cfg.hold_minutes} onChange={(e) => set("hold_minutes", e.target.value)} data-testid="pg-hold-minutes" />
        </Field>
        <Field label={t("op.default_deposit")}>
          <input type="number" step="0.001" className="input" value={cfg.default_deposit_percent} onChange={(e) => set("default_deposit_percent", e.target.value)} data-testid="pg-default-deposit" />
        </Field>
        <Field label={t("op.min_payment")}>
          <input type="number" step="0.001" className="input" value={cfg.min_online_payment} onChange={(e) => set("min_online_payment", e.target.value)} data-testid="pg-min-payment" />
        </Field>
        <Field label={t("op.link_days")}>
          <input type="number" min={1} className="input" value={cfg.link_expiry_days} onChange={(e) => set("link_expiry_days", e.target.value)} data-testid="pg-link-days" />
        </Field>
      </div>
      <div className="grid gap-3">
        {KEYS.map(([k, l]) => <KeyField key={k} k={k} label={l} cfg={cfg} t={t} value={keys[k] || ""} onChange={(v) => setKeys({ ...keys, [k]: v })} />)}
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("op.keys_note")}</p>
      </div>
      <div className="text-sm">{t("op.webhook")} <code className="mono text-xs" dir="ltr" data-testid="pg-webhook-url">{cfg.webhook_url}</code></div>
      <div className="flex gap-2 flex-wrap">
        <button className="btn-primary" onClick={save} disabled={busy} data-testid="pg-save">{busy ? t("op.saving") : t("op.save")}</button>
        <button className="btn-secondary" onClick={test} data-testid="pg-test">{t("op.test")}</button>
      </div>
      {testMsg && <div className="text-sm" data-testid="pg-test-result">{testMsg}</div>}
    </div>
  );
}
